const UUID_PATTERN =
  /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i;

const NEST_EXCEPTION_PREFIX = /^[A-Z][a-zA-Z]*Exception:\s*/;

const BARE_STATUS_PHRASE =
  /^(not found|bad request|unauthorized|forbidden|conflict|gone|too many requests|payload too large|request timeout|internal server error|bad gateway|service unavailable|gateway timeout)$/i;

function isTechnicalErrorMessage(raw: string): boolean {
  const text = raw.trim();
  if (!text) return true;

  if (NEST_EXCEPTION_PREFIX.test(text)) return true;
  if (/\bException:\s*/i.test(text)) return true;
  if (UUID_PATTERN.test(text)) return true;
  if (/^cannot\s+(get|post|put|patch|delete)\s+/i.test(text)) return true;
  if (/^<\s*!doctype\s+html/i.test(text)) return true;
  if (/\b(at |\/src\/|node_modules|\.ts:\d+|\.js:\d+)/i.test(text)) return true;
  if (
    /\b(ECONNREFUSED|ETIMEDOUT|EPIPE|ENOTFOUND|QueryFailedError)\b/.test(text)
  ) {
    return true;
  }
  if (/\bstatusCode\s*[:=]\s*\d+/i.test(text)) return true;
  if (text.length <= 48 && BARE_STATUS_PHRASE.test(text)) return true;

  // Provider / LLM errors (must not show API keys or OpenAI JSON to end users)
  if (/\bOpenAI returned HTTP\b/i.test(text)) return true;
  if (/\binvalid_api_key\b/i.test(text)) return true;
  if (/\bincorrect api key\b/i.test(text)) return true;
  if (/\binvalid_request_error\b/i.test(text)) return true;
  if (/\bplatform\.openai\.com\b/i.test(text)) return true;
  if (/\bsk-(?:proj-|svcacct-)?[A-Za-z0-9_*]{8,}/.test(text)) return true;
  if (/^\s*\{[\s\S]*"error"\s*:/.test(text)) return true;
  if (/^\s*Error:\s*\d{3}\b/.test(text)) return true;

  return false;
}

/**
 * Keeps intentional domain reasons; replaces provider/framework noise with
 * `fallback` (e.g. upload-entry suggestions unavailable copy).
 */
export function toUserFacingDomainOrFallbackMessage(
  rawMessage: string | null | undefined,
  fallback: string,
): string {
  const combined = (rawMessage ?? '').trim();
  if (combined && !isTechnicalErrorMessage(combined)) {
    return combined;
  }
  return fallback;
}

function defaultMessageForHttpStatus(status: number): string {
  if (status === 400) {
    return 'Something in your request was not valid. Please check and try again.';
  }
  if (status === 401) {
    return 'Please sign in again to continue.';
  }
  if (status === 403) {
    return 'You do not have permission to do this.';
  }
  if (status === 404) {
    return "That information isn't available right now. Try again in a moment.";
  }
  if (status === 408) {
    return 'That took too long. Please try again.';
  }
  if (status === 409) {
    return 'This conflicts with the latest data. Refresh and try again.';
  }
  if (status === 413) {
    return 'That file is too large. Choose a smaller file and try again.';
  }
  if (status === 429) {
    return "You're doing that too quickly. Please wait a moment and try again.";
  }
  if (status === 502 || status === 503 || status === 504) {
    return 'The service is temporarily unavailable. Please try again shortly.';
  }
  if (status >= 500 && status < 600) {
    return 'The server had a problem. Please try again shortly.';
  }
  return 'Something went wrong. Please try again.';
}

/**
 * Maps framework/plain-text HTTP failures (e.g. `ThrottlerException: Too Many
 * Requests`, bare `Not Found`) to short copy suitable for toasts. Keeps API
 * messages that already look like real validation or domain text.
 */
export function toUserFacingHttpFailureMessage(
  status: number,
  rawMessage: string | undefined,
): string {
  const combined = (rawMessage ?? '').trim();

  if (combined && !isTechnicalErrorMessage(combined)) {
    return combined;
  }

  return defaultMessageForHttpStatus(status);
}

/** Maps thrown values from `clientAuthorized*` helpers to user-facing copy. */
export function toUserFacingHttpFailureMessageFromUnknown(
  error: unknown,
): string {
  if (error instanceof Error && 'status' in error) {
    const status = (error as Error & { status: unknown }).status;
    if (typeof status === 'number' && Number.isFinite(status)) {
      return error.message;
    }
  }
  if (error instanceof Error) {
    return toUserFacingHttpFailureMessage(0, error.message);
  }
  return toUserFacingHttpFailureMessage(0, undefined);
}
